<template>
  <v-container>
    <v-alert type="error" v-if="loginError && !isLoggedIn">{{ loginError }}</v-alert>

    <!-- Už přihlášený uživatel (údaje z Pinia / localStorage) -->
    <div v-if="isLoggedIn" class="form logged-in">
      <p class="text-body-1">
        {{
          $t('login.already_logged_in', {
            firstName: displayFirstName,
            lastName: displayLastName,
            clubIndex: displayClubIndex,
          })
        }}
      </p>
      <v-btn color="primary" class="mt-4" :loading="logoutLoading" @click="onLogout">
        {{ $t('login.logout') }}
      </v-btn>
    </div>

    <template v-else>
    <!-- Formulář pro přihlášení -->
    <v-form v-if="showLoginForm" @submit.prevent="login" class="form">
      <h2>{{ $t('login.title') }}</h2>
      <v-text-field v-model="email" type="email" label="Email" required></v-text-field>
      <v-text-field 
        v-model="password" 
        :type="visible ? 'text' : 'password'"
        :label="$t('login.password')"
        autocomplete="current-password"
        :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append-inner="visible = !visible">
      </v-text-field>     
      <div class="d-flex justify-space-between align-center">
        <v-btn v-if="!isLoading" type="submit" color="primary">{{ $t('login.login') }}</v-btn>
        <v-progress-circular v-else indeterminate color="primary"></v-progress-circular>
        <v-btn text @click="toggleForm">{{ $t('login.forgot_password') }}</v-btn>
      </div>
    </v-form>

    <!-- Formulář pro obnovu hesla -->
    <v-form v-else @submit.prevent="resetPassword" class="form">
      <h2>{{ $t('login.password_reset_title') }}</h2>

      <v-text-field v-model="resetEmail" label="Email" required :rules="emailRules"></v-text-field>
      <div class="d-flex justify-space-between align-center">
        <v-btn :disabled="!isEmailValid" type="submit" color="primary">{{ $t('login.password_reset_label') }}</v-btn>
        <v-btn text @click="toggleForm">{{ $t('login.remember_password') }}</v-btn>
      </div>
    </v-form>
    </template>
  </v-container>
</template>




<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router'; 
import { useAuthStore } from '/auth.js';
import apiClient from '/api.js';
import { useI18n } from 'vue-i18n';
import { useSnackbarStore } from '@/stores/snackbarStore';

const { t } = useI18n();
const snackbar = useSnackbarStore();
const email = ref('');
const password = ref('');
const loginError = ref(''); 
const isLoading = ref(false); 
const logoutLoading = ref(false);

const authStore = useAuthStore();
const { isLoggedIn, firstName, last_name, clubIndex } = storeToRefs(authStore);

const displayFirstName = computed(() => firstName.value?.trim() || '—');
const displayLastName = computed(() => last_name.value?.trim() || '—');
const displayClubIndex = computed(() => {
  const ix = clubIndex.value;
  return ix != null && String(ix).trim() !== '' ? String(ix).trim() : '—';
});

const router = useRouter();

const resetEmail = ref('');
const showLoginForm = ref(true);


// Stav pro viditelnost hesla
const visible = ref(false);


// pravidlo pro tlačítko
const isEmailValid = computed(() => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.value);
});

// pravidlo pro pole email
const emailRules = [
  v => !!v || t('rules.required'), // Kontrola, zda je pole vyplněno
  v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || t('rules.invalid_email') // Kontrola formátu emailu
];

const toggleForm = () => {
  showLoginForm.value = !showLoginForm.value; // Přepne zobrazení formulářů
  loginError.value = ''; // Resetuje chybové hlášení
};


const login = async () => {
  loginError.value = ''; // Resetujte chybu před pokusem o přihlášení
  isLoading.value = true;
  try {
    await authStore.login(email.value, password.value);
    router.push('/');
    // Přesměrujte uživatele na domovskou stránku nebo jinam po úspěšném přihlášení
  } catch (error) {
    loginError.value = t('login.login_error');
    if (error.response && error.response.data) {
      // Pokud server vrátí specifickou chybovou zprávu, zobrazte ji
      loginError.value = error.response.data.detail || t('errors.request_failed');
    }
  }
  finally {
    isLoading.value = false; // Nastavte stav načítání zpět na false
  }
};

const onLogout = async () => {
  logoutLoading.value = true;
  try {
    await authStore.logout();
  } finally {
    logoutLoading.value = false;
  }
};

const resetPassword = async () => {
  try {
    await apiClient.post('password_reset/', { email: resetEmail.value });
    resetEmail.value = '';
    showLoginForm.value = true;
    snackbar.success(t('login.reset_message'));
    // Po úspěšném odeslání resetu můžete přepnout formulář nebo jinak reagovat
  } catch (error) {
    if (error.response && error.response.data) {
      loginError.value = error.response.data.detail || t('errors.request_failed');
    }
  }
};

</script>


<style scoped>
.form {
  max-width: 600px;
  padding-top: 40px;
  margin: 0px auto
}
.logged-in {
  padding-top: 40px;
}
</style>
